import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Icon } from "./Icon";

/**
 * ═══ CE QU'ON FAIT RAREMENT, RANGÉ DERRIÈRE UN BOUTON ═══
 *
 * Une ligne de régie portait six boutons de même poids : modifier, rouvrir,
 * terminer, cacher les résultats, vider, supprimer. Sur un écran qu'on pilote sous
 * pression, six cibles identiques ne se lisent pas — on cherche, et pendant qu'on
 * cherche la salle attend. Ce menu garde les gestes rares atteignables sans les
 * laisser concourir avec celui qu'on vient faire.
 *
 * ⚠️ CE N'EST PAS UN `<select>` DÉGUISÉ. Un menu déclenche des ACTIONS, il ne
 * choisit pas une valeur : d'où `role="menu"` et non une liste d'options, et d'où
 * la fermeture après chaque activation.
 *
 * ⚠️ CE QUI SE PERD QUAND ON ÉCRIT CE COMPOSANT À LA MAIN, et qui est ici : Échap
 * ferme, un clic dehors ferme, les flèches parcourent, le focus revient sur le
 * bouton à la fermeture (sinon il repart au début du document, et on a perdu sa
 * ligne), et le bouton dit qu'il ouvre quelque chose (`aria-haspopup`).
 *
 * Displayed strings are French: this is a French product.
 */
export function Menu({
  label,
  children,
}: {
  /** Ce que le bouton annonce : « Autres actions sur "Le quiz" ». */
  label: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const list = useRef<HTMLDivElement | null>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;

    const outside = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const keys = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        /* ⚠️ LE FOCUS REVIENT AU BOUTON. Sans ça il repart à la racine du document,
           et l'organisateur qui ferme par Échap a perdu la ligne où il était. */
        trigger.current?.focus();
        return;
      }
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      const items = [
        ...(list.current?.querySelectorAll<HTMLButtonElement>(
          'button:not([disabled])',
        ) ?? []),
      ];
      if (!items.length) return;
      e.preventDefault();
      /* ⚠️ À L'OUVERTURE, LE FOCUS EST ENCORE SUR LE BOUTON, donc hors de la liste :
         `indexOf` rend -1. Le calcul relatif marchait par accident vers le bas
         (-1 + 1 = 0) et se trompait vers le haut (-2, qui se replie sur l'AVANT-
         dernière entrée). D'où le départ écrit, plutôt que déduit. */
      const at = items.indexOf(document.activeElement as HTMLButtonElement);
      const down = e.key === "ArrowDown";
      const next =
        at < 0 ? (down ? 0 : items.length - 1) : down ? at + 1 : at - 1;
      items[(next + items.length) % items.length]?.focus();
    };

    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", keys);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", keys);
    };
  }, [open]);

  return (
    <div
      className="menu"
      ref={root}
      /* La fermeture après activation vit ici plutôt que sur chaque entrée : posée
         sur chacune, elle finit par manquer sur celle qu'on ajoute plus tard. */
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("button[role='menuitem']")) {
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        className="menu__b"
        ref={trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="plus" />
      </button>

      {open && (
        <div className="menu__l" id={id} role="menu" ref={list} aria-label={label}>
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * UNE ENTRÉE DU MENU.
 *
 * `danger` pour ce qui détruit : elle prend la couleur d'alerte et se range en bas,
 * derrière un filet. Le geste reste offert — on supprime vraiment des activités —
 * mais il ne ressemble plus à ses voisins.
 */
export function MenuItem({
  children,
  danger = false,
  ...rest
}: {
  children: ReactNode;
  danger?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      role="menuitem"
      {...rest}
      className={`menu__i${danger ? " menu__i--danger" : ""}`}
    >
      {children}
    </button>
  );
}

/** Le filet qui sépare ce qui pilote de ce qui détruit. */
export function MenuSep() {
  return <hr className="menu__s" />;
}
